import React, { useState, useEffect, useCallback } from 'react';
import { maintenanceApi, vehicleApi, tireApi, formatApiError } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wrench, Plus, PencilSimple, Trash, Paperclip, DownloadSimple,
  Warning, CheckCircle, Calendar, CurrencyEur, Car, MagnifyingGlass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const TYPE_LABELS = {
  tuev: 'TÜV / HU',
  inspektion: 'Inspektion / Service',
  reifenwechsel: 'Reifenwechsel',
  oelwechsel: 'Ölwechsel',
  reparatur: 'Reparatur',
  versicherung: 'Versicherung',
  sonstige: 'Sonstige',
};

const STATUS_LABELS = {
  planned: 'Geplant',
  in_progress: 'Läuft',
  done: 'Erledigt',
};

const STATUS_STYLES = {
  planned: 'bg-[#FFB800]/15 text-[#7A5500] border border-[#FFB800]/30',
  in_progress: 'bg-[#1976D2]/15 text-[#0B4F94] border border-[#1976D2]/30',
  done: 'bg-[#009951]/15 text-[#005E32] border border-[#009951]/30',
};

const TYPE_COLORS = {
  tuev: 'bg-[#E60000]/10 text-[#E60000]',
  inspektion: 'bg-[#1976D2]/10 text-[#1976D2]',
  reifenwechsel: 'bg-[#7C3AED]/10 text-[#7C3AED]',
  oelwechsel: 'bg-[#D97706]/10 text-[#D97706]',
  reparatur: 'bg-[#E60000]/10 text-[#E60000]',
  versicherung: 'bg-[#009951]/10 text-[#009951]',
  sonstige: 'bg-[#525866]/10 text-[#525866]',
};

const formatEur = (v) => v == null || v === '' ? '—'
  : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const formatDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('de-DE'); } catch { return s.slice(0, 10); }
};
const daysUntil = (s) => {
  if (!s) return null;
  const a = new Date(s); const b = new Date();
  return Math.floor((a - b) / (1000 * 60 * 60 * 24));
};

const emptyForm = {
  vehicle_id: '',
  type: 'tuev',
  title: '',
  performed_at: '',
  performed_at_mileage: '',
  next_due_at: '',
  next_due_at_mileage: '',
  provider: '',
  cost: '',
  notes: '',
  status: 'done',
};

const MaintenanceForm = ({ open, onClose, vehicles, editing, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        vehicle_id: editing.vehicle_id || '',
        type: editing.type || 'tuev',
        title: editing.title || '',
        performed_at: (editing.performed_at || '').slice(0, 10),
        performed_at_mileage: editing.performed_at_mileage ?? '',
        next_due_at: (editing.next_due_at || '').slice(0, 10),
        next_due_at_mileage: editing.next_due_at_mileage ?? '',
        provider: editing.provider || '',
        cost: editing.cost ?? '',
        notes: editing.notes || '',
        status: editing.status || 'done',
      });
    } else {
      setForm(emptyForm);
    }
    setFile(null);
  }, [open, editing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Bitte Fahrzeug wählen'); return; }
    if (!form.type) { toast.error('Bitte Typ wählen'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        performed_at_mileage: form.performed_at_mileage === '' ? null : parseInt(form.performed_at_mileage),
        next_due_at_mileage: form.next_due_at_mileage === '' ? null : parseInt(form.next_due_at_mileage),
        cost: form.cost === '' ? null : parseFloat(form.cost),
        performed_at: form.performed_at || null,
        next_due_at: form.next_due_at || null,
      };
      let saved;
      if (editing) {
        const res = await maintenanceApi.update(editing.id, payload);
        saved = res.data;
      } else {
        const res = await maintenanceApi.create(payload);
        saved = res.data;
      }
      // Optional file upload after save
      if (file) {
        setUploadProgress(true);
        try { await maintenanceApi.uploadAttachment(saved.id, file); }
        catch { toast.error('Anhang-Upload fehlgeschlagen'); }
        setUploadProgress(false);
      }
      toast.success(editing ? 'Wartung aktualisiert' : 'Wartung erstellt');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Wrench weight="fill" size={20} className="text-[#E60000]" />
            {editing ? 'Wartung bearbeiten' : 'Neue Wartung'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Fahrzeug *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => set('vehicle_id', v)}>
                <SelectTrigger data-testid="m-vehicle"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.brand} {v.model} ({v.license_plate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Typ *</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="m-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline">Titel / Bezeichnung</Label>
            <Input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder='z. B. "60.000 km Service"' data-testid="m-title" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Durchgeführt am</Label>
              <Input type="date" value={form.performed_at} onChange={e => set('performed_at', e.target.value)} data-testid="m-performed-at" />
            </div>
            <div>
              <Label className="overline">Kilometerstand</Label>
              <Input type="number" min={0} value={form.performed_at_mileage} onChange={e => set('performed_at_mileage', e.target.value)} placeholder="z. B. 60000" data-testid="m-performed-mileage" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Nächste Fälligkeit (Datum)</Label>
              <Input type="date" value={form.next_due_at} onChange={e => set('next_due_at', e.target.value)} data-testid="m-next-due-at" />
            </div>
            <div>
              <Label className="overline">Nächste Fälligkeit (km)</Label>
              <Input type="number" min={0} value={form.next_due_at_mileage} onChange={e => set('next_due_at_mileage', e.target.value)} placeholder="z. B. 75000" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="overline">Werkstatt / Anbieter</Label>
              <Input value={form.provider} onChange={e => set('provider', e.target.value)} placeholder="z. B. Bosch Service" />
            </div>
            <div>
              <Label className="overline">Kosten (€)</Label>
              <Input type="number" step="0.01" value={form.cost} onChange={e => set('cost', e.target.value)} />
            </div>
            <div>
              <Label className="overline">Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="m-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline">Notizen</Label>
            <textarea
              rows={3}
              className="w-full border border-[#E2E4E9] rounded-sm p-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#E60000]"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="z. B. Bremsbeläge vorne erneuert, Wischblätter getauscht…"
            />
          </div>

          <div>
            <Label className="overline">Rechnung / Beleg anhängen</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
            {file && <p className="text-xs text-[#525866] mt-1">{file.name}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
            <Button type="submit" className="btn-primary" disabled={saving || uploadProgress} data-testid="m-submit">
              {saving ? 'Speichere…' : (editing ? 'Aktualisieren' : 'Erstellen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Maintenance = () => {
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [tireReminders, setTireReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ vehicle_id: 'all', type: 'all', status: 'all', search: '' });
  const [dialog, setDialog] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.vehicle_id !== 'all') params.vehicle_id = filter.vehicle_id;
      if (filter.type !== 'all') params.type = filter.type;
      if (filter.status !== 'all') params.status = filter.status;
      const [m, v, tr] = await Promise.all([
        maintenanceApi.list(params),
        vehicleApi.getAll(),
        tireApi.reminders().catch(() => ({ data: { items: [] } })),
      ]);
      setRecords(m.data || []);
      setVehicles(v.data || []);
      setTireReminders(tr.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filter.vehicle_id, filter.type, filter.status]);

  useEffect(() => { load(); }, [load]);

  const filteredRecords = records.filter(r => {
    if (!filter.search.trim()) return true;
    const q = filter.search.toLowerCase();
    return (r.title || '').toLowerCase().includes(q) ||
           (r.provider || '').toLowerCase().includes(q) ||
           (r.notes || '').toLowerCase().includes(q) ||
           (r.vehicle?.license_plate || '').toLowerCase().includes(q);
  });

  // Stats
  const total = records.length;
  const planned = records.filter(r => r.status === 'planned').length;
  const overdue = records.filter(r => r.status !== 'done' && r.next_due_at && r.next_due_at < new Date().toISOString().slice(0, 10)).length;
  const totalCost = records.reduce((sum, r) => sum + Number(r.cost || 0), 0);

  const handleDelete = async (rec) => {
    if (!window.confirm(`Wartung "${rec.title || TYPE_LABELS[rec.type]}" wirklich löschen?`)) return;
    try {
      await maintenanceApi.remove(rec.id);
      toast.success('Wartung gelöscht');
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="maintenance-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111418]">Wartungscenter</h1>
          <p className="text-sm text-[#525866]">TÜV, Inspektionen, Reparaturen & Termine für alle Fahrzeuge</p>
        </div>
        <Button className="btn-primary" onClick={() => setDialog({ open: true, editing: null })} data-testid="add-maintenance-btn">
          <Plus weight="bold" size={16} className="mr-2" /> Neue Wartung
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card-swiss p-4">
          <p className="overline text-[#8A94A6]">Gesamt</p>
          <p className="text-2xl font-bold text-[#111418]">{total}</p>
        </div>
        <div className="card-swiss p-4">
          <p className="overline text-[#8A94A6]">Geplant</p>
          <p className="text-2xl font-bold text-[#FFB800]">{planned}</p>
        </div>
        <div className="card-swiss p-4">
          <p className="overline text-[#8A94A6]">Überfällig</p>
          <p className="text-2xl font-bold text-[#E60000]">{overdue}</p>
        </div>
        <div className="card-swiss p-4">
          <p className="overline text-[#8A94A6]">Kosten gesamt</p>
          <p className="text-2xl font-bold text-[#111418]">{formatEur(totalCost)}</p>
        </div>
      </div>

      {/* Anstehende Reifenwechsel */}
      {tireReminders.length > 0 && (
        <div className="card-swiss p-4" data-testid="tire-reminders-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold tracking-wide text-[#111418] flex items-center gap-2">
              <Wrench weight="fill" size={14} className="text-[#7C3AED]" />
              Anstehende Reifenwechsel <span className="badge-neutral">{tireReminders.length}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {tireReminders.slice(0, 12).map((t) => (
              <Link
                key={t.vehicle_id}
                to={`/vehicles/${t.vehicle_id}`}
                data-testid={`tire-reminder-${t.vehicle_id}`}
                className={`p-3 rounded border ${t.is_overdue ? 'border-red-300 bg-red-50/50' : t.days_until <= 7 ? 'border-amber-300 bg-amber-50/50' : 'border-[#E2E4E9] bg-white'} hover:shadow-sm transition`}
              >
                <div className="text-sm font-semibold text-[#111418]">{t.brand} {t.model}</div>
                <div className="text-xs text-[#525866]">{t.license_plate}</div>
                <div className="text-xs mt-1">
                  <span className="text-[#525866]">→ Wechsel auf </span>
                  <span className="font-semibold text-[#111418]">{{summer:'Sommerreifen',winter:'Winterreifen'}[t.target_type]}</span>
                </div>
                <div className={`text-xs mt-1 font-semibold ${t.is_overdue ? 'text-red-700' : t.days_until <= 7 ? 'text-amber-700' : 'text-[#525866]'}`}>
                  {t.is_overdue ? `Überfällig um ${Math.abs(t.days_until)} Tag(e)` : `in ${t.days_until} Tag(en)`}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
          <Input value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} placeholder="Suche Titel, Werkstatt, Kennzeichen…" className="pl-8 h-9" data-testid="m-search" />
        </div>
        <div className="min-w-[200px]">
          <Select value={filter.vehicle_id} onValueChange={v => setFilter(f => ({ ...f, vehicle_id: v }))}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Alle Fahrzeuge" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Alle Fahrzeuge</SelectItem>
              {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={filter.type} onValueChange={v => setFilter(f => ({ ...f, type: v }))}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Alle Typen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px]">
          <Select value={filter.status} onValueChange={v => setFilter(f => ({ ...f, status: v }))}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Alle Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="card-swiss overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#8A94A6]">Lade Wartungen…</div>
        ) : filteredRecords.length === 0 ? (
          <div className="p-12 text-center text-[#8A94A6]">
            <Wrench weight="thin" size={48} className="mx-auto mb-3 opacity-50" />
            Noch keine Wartungseinträge erfasst.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8FA] text-[#525866]">
                <tr>
                  <th className="text-left p-3 font-semibold">Fahrzeug</th>
                  <th className="text-left p-3 font-semibold">Typ / Titel</th>
                  <th className="text-left p-3 font-semibold">Durchgeführt</th>
                  <th className="text-left p-3 font-semibold">Nächste Fälligkeit</th>
                  <th className="text-right p-3 font-semibold">Kosten</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="p-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((r) => {
                  const dleft = daysUntil(r.next_due_at);
                  const isOverdue = r.status !== 'done' && dleft !== null && dleft < 0;
                  const isSoon = r.status !== 'done' && dleft !== null && dleft >= 0 && dleft <= 30;
                  return (
                    <tr key={r.id} className="border-t border-[#E2E4E9] hover:bg-[#FAFAFB]" data-testid={`m-row-${r.id}`}>
                      <td className="p-3">
                        {r.vehicle ? (
                          <Link to={`/vehicles/${r.vehicle.id}`} className="hover:text-[#E60000]">
                            <p className="font-semibold text-[#111418]">{r.vehicle.brand} {r.vehicle.model}</p>
                            <p className="text-xs text-[#525866] font-mono">{r.vehicle.license_plate}</p>
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded font-semibold ${TYPE_COLORS[r.type] || TYPE_COLORS.sonstige}`}>
                            {TYPE_LABELS[r.type] || r.type}
                          </span>
                          {r.attachments?.length > 0 && (
                            <Paperclip size={12} className="text-[#8A94A6]" title={`${r.attachments.length} Anhang`} />
                          )}
                        </div>
                        <p className="text-sm text-[#111418] mt-1">{r.title || '—'}</p>
                        {r.provider && <p className="text-xs text-[#525866]">{r.provider}</p>}
                      </td>
                      <td className="p-3">
                        <p>{formatDate(r.performed_at)}</p>
                        {r.performed_at_mileage != null && (
                          <p className="text-xs text-[#525866]">{r.performed_at_mileage.toLocaleString('de-DE')} km</p>
                        )}
                      </td>
                      <td className="p-3">
                        {r.next_due_at ? (
                          <>
                            <p className={isOverdue ? 'text-[#E60000] font-semibold' : (isSoon ? 'text-[#FFB800] font-semibold' : '')}>
                              {formatDate(r.next_due_at)}
                            </p>
                            <p className="text-xs">
                              {isOverdue ? <span className="text-[#E60000]">{Math.abs(dleft)} Tage überfällig</span>
                                : isSoon ? <span className="text-[#FFB800]">in {dleft} Tagen</span>
                                : <span className="text-[#525866]">in {dleft} Tagen</span>}
                            </p>
                          </>
                        ) : '—'}
                        {r.next_due_at_mileage != null && (
                          <p className="text-xs text-[#525866]">bei {r.next_due_at_mileage.toLocaleString('de-DE')} km</p>
                        )}
                      </td>
                      <td className="p-3 text-right tabular-nums">{formatEur(r.cost)}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLES[r.status] || ''}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, editing: r })} data-testid={`m-edit-${r.id}`}>
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} className="text-[#E60000]" data-testid={`m-del-${r.id}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MaintenanceForm
        open={dialog.open}
        onClose={() => setDialog({ open: false, editing: null })}
        vehicles={vehicles}
        editing={dialog.editing}
        onSaved={load}
      />
    </div>
  );
};

export default Maintenance;
